import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ProfileImage from '../profileImage';

const UserSearchBar = ({ onSearch, searchResults, isLoading }) => {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const [showResults, setShowResults] = useState(false);

  const handleInputChange = (e) => {
    const value = e.target.value;
    setQuery(value);
    setShowResults(value.length >= 2);
    onSearch(value);
  };

  const clearSearch = () => {
    setQuery('');
    setShowResults(false);
    onSearch('');
  };

  return (
    <div className="bg-white rounded-2xl shadow-lg p-6 relative">
      <div className="relative">
        <input
          type="text"
          value={query}
          onChange={handleInputChange}
          placeholder={t("feed.searchUsers")}
          className="w-full px-4 py-3 pl-12 pr-10 border-2 border-gray-100 rounded-xl
            focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent
            placeholder:text-gray-400 transition-all"
        />
        
        {/* Search Icon */}
        <div className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400">
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" 
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" 
            />
          </svg>
        </div>

        {/* Clear Button */}
        {query && (
          <button
            onClick={clearSearch}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}

        {/* Loading Spinner */}
        {isLoading && (
          <div className="absolute right-3 top-1/2 -translate-y-1/2">
            <div className="animate-spin rounded-full h-4 w-4 border-2 border-[#E05720] border-t-transparent"></div>
          </div>
        )}
      </div>

      {/* Search Results Dropdown */}
      {showResults && query.length >= 2 && (
        <div className="absolute left-6 right-6 top-full mt-2 bg-white border border-gray-200 rounded-xl shadow-lg z-50 max-h-80 overflow-y-auto">
          {isLoading ? (
            <div className="p-4 text-center">
              <div className="animate-spin rounded-full h-6 w-6 border-2 border-[#E05720] border-t-transparent mx-auto mb-2"></div>
              <p className="text-gray-500 text-sm">{t("feed.searching")}</p>
            </div>
          ) : searchResults && searchResults.length > 0 ? (
            <div className="py-2">
              {searchResults.map((user) => (
                <Link
                  key={user._id}
                  to={`/feed/${user._id}`}
                  onClick={() => {
                    setShowResults(false);
                    setQuery('');
                  }}
                  className="flex items-center space-x-3 px-4 py-3 hover:bg-[#E05720]/5 transition-colors rounded-lg mx-2"
                >
                  <div className="relative">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-r from-[#E05720] to-[#E05720]/80 p-0.5">
                      <ProfileImage
                        image={user.profilePicture}
                        user={user}
                        alt={user.userName}
                        className="w-full h-full rounded-full object-cover bg-white"
                      />
                    </div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      @{user.userName}
                    </p>
                    {user.memberSince && (
                      <p className="text-xs text-gray-400 truncate mt-1">
                        {t("feed.memberSince")} {new Date(user.memberSince).toLocaleDateString()}
                      </p>
                    )}
                  </div>
                  <div className="text-[#E05720]" title={t("feed.viewProfile")}>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="p-4 text-center">
              <div className="text-gray-400 mb-2">
                <svg className="w-8 h-8 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
              </div>
              <p className="text-gray-500 text-sm">{t("feed.noUsersFound")}</p>
              <p className="text-gray-400 text-xs mt-1">{t("feed.tryDifferentTerm")}</p>
            </div>
          )}
        </div>
      )}

      {/* Backdrop to close results */}
      {showResults && (
        <div 
          className="fixed inset-0 z-40" 
          onClick={() => setShowResults(false)}
        />
      )}
    </div>
  );
};

export default UserSearchBar; 